import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/adminApi';

function AdminGatheringAvailability({ adminToken, tabName }) {
  // Form state
  const [formData, setFormData] = useState({
    date: '',
    theme_id: '',
    location_id: '',
    reason: '',
  });

  // UI state
  const [unavailabilityRecords, setUnavailabilityRecords] = useState([]);
  const [themes, setThemes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Load data when tab is active
  useEffect(() => {
    if (tabName === 'GatheringAvailability') {
      loadThemes();
      loadLocations();
      loadUnavailability();
    }
  }, [tabName]);

  const loadThemes = async () => {
    try {
      const response = await fetch('/api/lookups/themes');
      if (response.ok) {
        const data = await response.json();
        setThemes(data);
      }
    } catch (err) {
      console.error('Failed to load themes:', err);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await fetch('/api/lookups/locations');
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      }
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadUnavailability = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getGatheringUnavailability(adminToken);
      setUnavailabilityRecords(data);
      setError('');
    } catch (err) {
      setError('Failed to load unavailability records: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date) {
      setError('Date is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        date: formData.date,
        theme_id: formData.theme_id || null,
        location_id: formData.location_id || null,
        reason: formData.reason || null,
      };

      await adminApi.createGatheringUnavailability(payload, adminToken);
      setSuccess('Unavailability record created successfully!');
      resetForm();
      loadUnavailability();
    } catch (err) {
      setError('Failed to create unavailability record: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this unavailability record?')) {
      return;
    }

    try {
      setLoading(true);
      await adminApi.deleteGatheringUnavailability(recordId, adminToken);
      setSuccess('Unavailability record deleted successfully!');
      loadUnavailability();
    } catch (err) {
      setError('Failed to delete unavailability record: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: '',
      theme_id: '',
      location_id: '',
      reason: '',
    });
    setShowForm(false);
    setError('');
    setSuccess('');
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>Gathering Availability Control</h2>

      <p style={{ color: '#666', marginBottom: '20px' }}>
        Mark specific dates, themes, or locations as unavailable for gatherings.
        Leave theme or location blank to apply to all themes/locations.
      </p>

      {error && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '10px',
          marginBottom: '20px',
          backgroundColor: '#d4edda',
          color: '#155724',
          borderRadius: '4px'
        }}>
          {success}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {showForm ? 'Hide Form' : 'Mark Date as Unavailable'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={{ marginBottom: '40px' }}>
          <h3>Create Unavailability Record</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Date *
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              min={getMinDate()}
              required
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
            <small style={{ color: '#666' }}>Cannot select past dates</small>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Theme (optional - leave blank for all themes)
            </label>
            <select
              name="theme_id"
              value={formData.theme_id}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            >
              <option value="">All Themes</option>
              {themes.map(theme => (
                <option key={theme.id} value={theme.id}>
                  {theme.theme_name}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Location (optional - leave blank for all locations)
            </label>
            <select
              name="location_id"
              value={formData.location_id}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            >
              <option value="">All Locations</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.city}, {location.area}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Reason (optional - internal note)
            </label>
            <textarea
              name="reason"
              value={formData.reason}
              onChange={handleInputChange}
              rows="3"
              placeholder="Holiday, special event, etc."
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 20px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Creating...' : 'Create'}
            </button>

            <button
              type="button"
              onClick={resetForm}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <h3>Unavailability Records</h3>
      {loading && <p>Loading...</p>}

      {unavailabilityRecords.length === 0 && !loading && (
        <p>No unavailability records created yet.</p>
      )}

      {unavailabilityRecords.length > 0 && (
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginTop: '20px'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Date</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Theme</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Location</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Reason</th>
              <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {unavailabilityRecords.map(record => (
              <tr key={record.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ padding: '10px' }}>
                  {new Date(record.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </td>
                <td style={{ padding: '10px' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: record.theme_id ? '#e7f3ff' : '#fff3cd',
                    color: record.theme_id ? '#004085' : '#856404'
                  }}>
                    {record.theme_name}
                  </span>
                </td>
                <td style={{ padding: '10px' }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: record.location_id ? '#e7f3ff' : '#fff3cd',
                    color: record.location_id ? '#004085' : '#856404'
                  }}>
                    {record.location_name}
                  </span>
                </td>
                <td style={{ padding: '10px' }}>{record.reason || '-'}</td>
                <td style={{ padding: '10px' }}>
                  <button
                    onClick={() => handleDelete(record.id)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminGatheringAvailability;
